import React, { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { Building2, Bot } from "lucide-react";
import { cn } from "../../../../utils/cn";

// Hilo del asistente interno: burbujas del usuario a la derecha, de la IA a la
// izquierda y avisos del sistema centrados en gris. Sin estados de entrega ni
// adjuntos: es una conversación de texto 1:1 con la IA del tenant.

// Separador temporal de un mensaje. El hilo es único por usuario y persiste
// (se crea al primer mensaje y no se reinicia), así que la hora sola sería
// ambigua: un mensaje de anteayer y uno de hoy se leerían igual. Se antepone la
// fecha cuando el mensaje no es de hoy. Sin año: es una conversación, no un
// histórico.
function formatTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  if (sameDay) return time;
  return `${date.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" })} ${time}`;
}

function Bubble({ message }) {
  const { t } = useTranslation();
  const time = formatTime(message.createdAt);

  if (message.role === "system") {
    return (
      <div className="flex justify-center">
        <p className="max-w-[85%] text-center text-[11px] leading-relaxed text-fg-muted">
          {message.content}
        </p>
      </div>
    );
  }

  const isUser = message.role === "user";

  return (
    <div className={cn("flex gap-2", isUser ? "justify-end" : "justify-start")}>
      {!isUser && (
        <span
          className="shrink-0 inline-flex w-7 h-7 items-center justify-center rounded-full bg-brand-50 text-brand-600"
          aria-hidden="true"
        >
          <Bot className="w-4 h-4" />
        </span>
      )}
      <div className={cn("max-w-[min(100%,42rem)] min-w-0", isUser && "flex flex-col items-end")}>
        <div
          className={cn(
            "rounded-2xl px-3.5 py-2 text-sm leading-relaxed break-words",
            isUser
              ? "hd-brand-solid bg-brand-500 text-white rounded-br-sm"
              : "bg-bg-muted text-fg rounded-bl-sm",
            // La burbuja optimista se atenúa hasta que el backend confirma el
            // mensaje: no es "enviado" todavía.
            message.pending && "opacity-60",
          )}
        >
          {/* Solo la IA puede mandar markdown (mismo criterio que AIChatWidget);
              lo que escribe el usuario se muestra literal. */}
          {!isUser && message.content ? (
            <div className="[&_p]:m-0 [&_p+p]:mt-2 [&_ul]:my-2 [&_ol]:my-2 [&_code]:text-[0.85em]">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
          ) : (
            <span className="whitespace-pre-wrap">{message.content}</span>
          )}
        </div>
        <span className="mt-0.5 px-1 text-[10px] text-fg-subtle">
          {message.pending ? t("Enviando…") : time}
        </span>
      </div>
    </div>
  );
}

// Indicador de "la IA está pensando". Tres puntos con la misma animación que el
// resto de la app (animate-pulse) en vez de un spinner: no bloquea la lectura
// del hilo.
function AwaitingBubble() {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 justify-start">
      <span
        className="shrink-0 inline-flex w-7 h-7 items-center justify-center rounded-full bg-brand-50 text-brand-600"
        aria-hidden="true"
      >
        <Bot className="w-4 h-4" />
      </span>
      <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm bg-bg-muted px-3.5 py-3">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="w-1.5 h-1.5 rounded-full bg-fg-subtle animate-pulse motion-reduce:animate-none"
            style={{ animationDelay: `${delay}ms` }}
            aria-hidden="true"
          />
        ))}
        <span className="sr-only">{t("El asistente está escribiendo…")}</span>
      </div>
    </div>
  );
}

/**
 * Hilo de mensajes. `role="log"` + `aria-live="polite"` hacen que cada mensaje
 * nuevo se anuncie sin robar el foco del redactor (el usuario sigue escribiendo
 * mientras llega la respuesta).
 *
 * @param {object} props
 * @param {Array} props.messages
 * @param {boolean} props.awaiting - El backend todavía no devolvió la respuesta.
 * @param {string|null} [props.agentName] - Nombre del agente, para el vacío.
 */
export default function InternalChatThread({ messages, awaiting, agentName }) {
  const { t } = useTranslation();
  const endRef = useRef(null);

  // Auto-scroll al último mensaje. "auto" y no "smooth" a propósito: con
  // mensajes que llegan tras un sondeo, el desplazamiento animado se ve como un
  // salto tardío.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages, awaiting]);

  return (
    <div
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-label={t("Conversación con el asistente interno")}
      className="flex flex-col gap-3 px-4 sm:px-6 py-4"
    >
      {messages.length === 0 && !awaiting && (
        <div className="flex flex-col items-center justify-center text-center py-10 px-4">
          <span
            className="mb-3 inline-flex w-12 h-12 items-center justify-center rounded-full bg-bg-muted text-fg-muted"
            aria-hidden="true"
          >
            <Building2 className="w-6 h-6" />
          </span>
          <p className="text-sm text-fg-muted max-w-md">
            {t(
              "Pregunta lo que necesites sobre la operación. La conversación es privada: solo tú ves este hilo."
            )}
          </p>
          {agentName && (
            <p className="mt-1 text-xs text-fg-subtle">
              {t("Atiende {{agente}}").replace("{{agente}}", agentName)}
            </p>
          )}
        </div>
      )}

      {messages.map((message) => (
        <Bubble key={message.id} message={message} />
      ))}

      {awaiting && <AwaitingBubble />}

      <div ref={endRef} />
    </div>
  );
}
